/**
 * Configuration page - Edit ladder and risk regime settings (admin only).
 */

import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import {
  Edit,
  Add,
  Delete,
  Save,
  PlayArrow,
} from '@mui/icons-material';
import { configApi, jobsApi, portfoliosApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { LadderConfig, RiskConfig, PortfolioMetadata } from '../types';

export function ConfigPage() {
  const { isAdmin } = useAuth();
  const [ladderConfigs, setLadderConfigs] = useState<Record<string, LadderConfig>>({});
  const [riskConfig, setRiskConfig] = useState<RiskConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingBucket, setEditingBucket] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Jobs section state
  const [portfolios, setPortfolios] = useState<PortfolioMetadata[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [jobLoading, setJobLoading] = useState<string | null>(null);
  const [jobMessage, setJobMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // System config state
  const [systemConfig, setSystemConfig] = useState<{
    providers: {
      portfolioProvider: string;
      marketDataProvider: string;
      macroDataProvider: string;
    };
    ai: {
      enabled: boolean;
      advisoryEnabled: boolean;
    };
  } | null>(null);
  const [systemConfigLoading, setSystemConfigLoading] = useState(false);
  const [systemConfigMessage, setSystemConfigMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (isAdmin) {
      loadConfigs();
      loadPortfolios();
      loadSystemConfig();
    }
  }, [isAdmin]);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      const [ladderData, riskData] = await Promise.all([
        configApi.getLadderConfigs(),
        configApi.getRiskConfig(),
      ]);
      setLadderConfigs(ladderData.ladder_configs);
      setRiskConfig(riskData.risk_config);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configurations');
      console.error('Config load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSystemConfig = async () => {
    try {
      setSystemConfigLoading(true);
      const response = await configApi.getSystemConfig();
      setSystemConfig(response.system_config);
    } catch (err) {
      console.error('Failed to load system config:', err);
      setSystemConfigMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to load system configuration',
      });
    } finally {
      setSystemConfigLoading(false);
    }
  };

  const handleUpdateSystemConfig = async () => {
    if (!systemConfig) return;
    
    try {
      setSystemConfigLoading(true);
      setSystemConfigMessage(null);
      await configApi.updateSystemConfig(systemConfig);
      setSystemConfigMessage({
        type: 'success',
        text: 'System configuration updated successfully',
      });
      await loadSystemConfig();
    } catch (err) {
      setSystemConfigMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to update system configuration',
      });
    } finally {
      setSystemConfigLoading(false);
    }
  };

  const loadPortfolios = async () => {
    try {
      const response = await portfoliosApi.list();
      setPortfolios(response.portfolios);
      if (response.portfolios.length > 0 && !selectedPortfolioId) {
        setSelectedPortfolioId(response.portfolios[0].portfolioId);
      }
    } catch (err) {
      console.error('Failed to load portfolios:', err);
    }
  };

  const handleTriggerJob = async (jobType: 'all-portfolios' | 'single-portfolio' | 'global-email' | 'portfolio-email') => {
    try {
      setJobLoading(jobType);
      setJobMessage(null);

      let result;
      if (jobType === 'all-portfolios') {
        result = await jobsApi.triggerPortfolioBatchAll();
      } else if (jobType === 'single-portfolio') {
        if (!selectedPortfolioId) {
          setJobMessage({ type: 'error', text: 'Please select a portfolio' });
          setJobLoading(null);
          return;
        }
        result = await jobsApi.triggerPortfolioBatchSingle(selectedPortfolioId);
      } else if (jobType === 'global-email') {
        result = await jobsApi.triggerGlobalEmail();
      } else if (jobType === 'portfolio-email') {
        result = await jobsApi.triggerPortfolioEmail();
        // Show summary if available
        if (result?.summary) {
          const { success, skipped, errors } = result.summary;
          setJobMessage({
            type: 'success',
            text: `${result.message || 'Portfolio emails triggered'}: ${success} sent, ${skipped} skipped, ${errors} errors`,
          });
          return;
        }
      }

      setJobMessage({
        type: 'success',
        text: result?.message || 'Job triggered successfully',
      });
    } catch (err) {
      setJobMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to trigger job',
      });
    } finally {
      setJobLoading(null);
    }
  };

  const handleEditLadder = (bucket: string) => {
    setEditingBucket(bucket);
    setEditDialogOpen(true);
  };

  const handleSaveLadder = async (config: LadderConfig) => {
    try {
      await configApi.updateLadderConfig(config);
      setEditDialogOpen(false);
      setEditingBucket(null);
      await loadConfigs();
    } catch (err) {
      console.error('Failed to save ladder config:', err);
      alert('Failed to save ladder configuration');
    }
  };

  if (!isAdmin) {
    return (
      <Alert severity="error">Admin access required to view configuration</Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Configuration
      </Typography>

      {/* Ladder Configurations */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Ladder Configurations</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Bucket</TableCell>
                  <TableCell>Steps</TableCell>
                  <TableCell>Max Position %</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(ladderConfigs).map(([bucket, config]) => (
                  <TableRow key={bucket}>
                    <TableCell>
                      <Chip label={bucket} />
                    </TableCell>
                    <TableCell>
                      {config.steps.length} step(s)
                    </TableCell>
                    <TableCell>
                      {config.maxPositionPct ? `${config.maxPositionPct}%` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {config.updatedAt
                        ? new Date(config.updatedAt).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditLadder(bucket)}
                      >
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Risk Config */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Risk Regime Thresholds
          </Typography>
          {riskConfig ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Risk configuration updates are not yet implemented. Thresholds are currently in code.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Current thresholds are managed in the RiskRegimeEngine code.
              </Typography>
            </Box>
          ) : (
            <Alert severity="warning">Risk configuration not available</Alert>
          )}
        </CardContent>
      </Card>

      {/* System Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure data providers and AI settings for the system.
          </Typography>

          {systemConfigMessage && (
            <Alert
              severity={systemConfigMessage.type}
              sx={{ mb: 2 }}
              onClose={() => setSystemConfigMessage(null)}
            >
              {systemConfigMessage.text}
            </Alert>
          )}

          {systemConfigLoading && !systemConfig ? (
            <CircularProgress />
          ) : systemConfig ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Data Providers */}
              <Box>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Data Providers
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Market Data Provider</InputLabel>
                    <Select
                      value={systemConfig.providers.marketDataProvider}
                      label="Market Data Provider"
                      onChange={(e) =>
                        setSystemConfig({
                          ...systemConfig,
                          providers: {
                            ...systemConfig.providers,
                            marketDataProvider: e.target.value,
                          },
                        })
                      }
                    >
                      <MenuItem value="mock">Mock</MenuItem>
                      <MenuItem value="schwab">Schwab</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Portfolio Provider</InputLabel>
                    <Select
                      value={systemConfig.providers.portfolioProvider}
                      label="Portfolio Provider"
                      onChange={(e) =>
                        setSystemConfig({
                          ...systemConfig,
                          providers: {
                            ...systemConfig.providers,
                            portfolioProvider: e.target.value,
                          },
                        })
                      }
                    >
                      <MenuItem value="mock">Mock</MenuItem>
                      <MenuItem value="schwab">Schwab</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Macro Data Provider</InputLabel>
                    <Select
                      value={systemConfig.providers.macroDataProvider}
                      label="Macro Data Provider"
                      onChange={(e) =>
                        setSystemConfig({
                          ...systemConfig,
                          providers: {
                            ...systemConfig.providers,
                            macroDataProvider: e.target.value,
                          },
                        })
                      }
                    >
                      <MenuItem value="mock">Mock</MenuItem>
                      <MenuItem value="fred">FRED</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Divider />

              {/* AI Settings */}
              <Box>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  AI Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body1">AI Enabled</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enable AI-powered analysis and recommendations
                      </Typography>
                    </Box>
                    <Select
                      value={systemConfig.ai.enabled ? 'enabled' : 'disabled'}
                      onChange={(e) =>
                        setSystemConfig({
                          ...systemConfig,
                          ai: {
                            ...systemConfig.ai,
                            enabled: e.target.value === 'enabled',
                          },
                        })
                      }
                      size="small"
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="enabled">Enabled</MenuItem>
                      <MenuItem value="disabled">Disabled</MenuItem>
                    </Select>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body1">AI Advisory Enabled</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enable AI advisory commentary in emails and reports
                      </Typography>
                    </Box>
                    <Select
                      value={systemConfig.ai.advisoryEnabled ? 'enabled' : 'disabled'}
                      onChange={(e) =>
                        setSystemConfig({
                          ...systemConfig,
                          ai: {
                            ...systemConfig.ai,
                            advisoryEnabled: e.target.value === 'enabled',
                          },
                        })
                      }
                      size="small"
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="enabled">Enabled</MenuItem>
                      <MenuItem value="disabled">Disabled</MenuItem>
                    </Select>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleUpdateSystemConfig}
                  disabled={systemConfigLoading}
                  startIcon={<Save />}
                >
                  {systemConfigLoading ? 'Saving...' : 'Save Configuration'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Alert severity="warning">System configuration not available</Alert>
          )}
        </CardContent>
      </Card>

      {/* Jobs Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Batch Jobs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manually trigger batch jobs that process portfolios and send emails.
          </Typography>

          {jobMessage && (
            <Alert
              severity={jobMessage.type}
              sx={{ mb: 2 }}
              onClose={() => setJobMessage(null)}
            >
              {jobMessage.text}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Run All Portfolios */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Run All Portfolios
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Triggers the portfolio daily batch orchestrator which processes all portfolios in parallel.
                Each portfolio will be processed asynchronously, fetching positions, calculating state,
                running ladder and trade engines, and generating recommendations. Portfolio-specific emails
                will be sent automatically as each portfolio completes.
              </Typography>
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={() => handleTriggerJob('all-portfolios')}
                disabled={jobLoading !== null}
              >
                {jobLoading === 'all-portfolios' ? 'Running...' : 'Run All Portfolios'}
              </Button>
            </Box>

            <Divider />

            {/* Run Single Portfolio */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Run Single Portfolio
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Triggers portfolio processing for a single selected portfolio. This will process the portfolio
                immediately, fetching positions, calculating state, running engines, and generating recommendations.
                A portfolio-specific email will be sent automatically when processing completes.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Select Portfolio</InputLabel>
                  <Select
                    value={selectedPortfolioId}
                    label="Select Portfolio"
                    onChange={(e) => setSelectedPortfolioId(e.target.value)}
                  >
                    {portfolios.map((portfolio) => (
                      <MenuItem key={portfolio.portfolioId} value={portfolio.portfolioId}>
                        {portfolio.name} ({portfolio.portfolioId})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={() => handleTriggerJob('single-portfolio')}
                  disabled={jobLoading !== null || !selectedPortfolioId}
                >
                  {jobLoading === 'single-portfolio' ? 'Running...' : 'Run Portfolio'}
                </Button>
              </Box>
            </Box>

            <Divider />

            {/* Run Global Email */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Run Global Daily Email
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Triggers the global daily summary email that provides an overview of all portfolios,
                aggregate performance metrics, and overall market commentary. This email is normally
                sent automatically at 4:30 PM ET, but can be triggered manually for testing or immediate delivery.
              </Typography>
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={() => handleTriggerJob('global-email')}
                disabled={jobLoading !== null}
              >
                {jobLoading === 'global-email' ? 'Running...' : 'Send Global Email'}
              </Button>
            </Box>

            <Divider />

            {/* Run Portfolio Emails */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Send Portfolio Emails
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Triggers portfolio detail emails for all portfolios that have email enabled and meet
                the frequency requirements (DAILY, WEEKLY on correct day, etc.). This sends the
                "Exelizer Portfolio Update" email for each eligible portfolio, allowing you to test
                email changes and see the current portfolio state.
              </Typography>
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={() => handleTriggerJob('portfolio-email')}
                disabled={jobLoading !== null}
              >
                {jobLoading === 'portfolio-email' ? 'Sending...' : 'Send Portfolio Emails'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Edit Ladder Dialog */}
      {editingBucket && ladderConfigs[editingBucket] && (
        <LadderConfigDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingBucket(null);
          }}
          config={ladderConfigs[editingBucket]}
          onSave={handleSaveLadder}
        />
      )}
    </Box>
  );
}

interface LadderConfigDialogProps {
  open: boolean;
  onClose: () => void;
  config: LadderConfig;
  onSave: (config: LadderConfig) => void;
}

function LadderConfigDialog({ open, onClose, config, onSave }: LadderConfigDialogProps) {
  const [steps, setSteps] = useState(config.steps);
  const [maxPositionPct, setMaxPositionPct] = useState(config.maxPositionPct?.toString() || '');

  const handleAddStep = () => {
    setSteps([
      ...steps,
      {
        thresholdPct: steps.length > 0 ? steps[steps.length - 1].thresholdPct + 25 : 25,
        trimPctOfPosition: 10,
        label: `STEP_${steps.length > 0 ? steps[steps.length - 1].thresholdPct + 25 : 25}`,
      },
    ]);
  };

  const handleRemoveStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const handleStepChange = (index: number, field: string, value: number | string) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  const handleSave = () => {
    const updatedConfig: LadderConfig = {
      ...config,
      steps,
      maxPositionPct: maxPositionPct ? parseFloat(maxPositionPct) : undefined,
    };
    onSave(updatedConfig);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Ladder Configuration: {config.bucket}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Max Position %"
            type="number"
            value={maxPositionPct}
            onChange={(e) => setMaxPositionPct(e.target.value)}
            sx={{ mb: 3 }}
          />
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Ladder Steps</Typography>
            <Button startIcon={<Add />} onClick={handleAddStep} size="small">
              Add Step
            </Button>
          </Box>
          {steps.map((step, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" gap={2} alignItems="center">
                  <TextField
                    label="Threshold %"
                    type="number"
                    value={step.thresholdPct}
                    onChange={(e) =>
                      handleStepChange(index, 'thresholdPct', parseFloat(e.target.value))
                    }
                    size="small"
                  />
                  <TextField
                    label="Trim %"
                    type="number"
                    value={step.trimPctOfPosition}
                    onChange={(e) =>
                      handleStepChange(index, 'trimPctOfPosition', parseFloat(e.target.value))
                    }
                    size="small"
                  />
                  <TextField
                    label="Label"
                    value={step.label}
                    onChange={(e) => handleStepChange(index, 'label', e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveStep(index)}
                    disabled={steps.length === 1}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<Save />}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}


